import React, { useState, ChangeEvent, SetStateAction, useEffect, MouseEvent } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomPopup from '../components/BtAdminPanel/CustomPopup';
import Button from '../library/Button/button';
import { UserType } from '../utils/Helper/enums';
import UserLogin from '../utils/UserLogin/UserLogin';
import WelcomeHeader from '../components/TopBar/WelcomeHeader';
import { encrypt } from '../utils/Helper/encryption';
import { wrapAsyncFunction } from '../components/UtilityFunction/wrapAsyncFunction';
import InputField from '../library/inputField/inputField';
import backgroundImage from './login-1200-800.jpeg';
import { adminRoutes, btAdminRoutes, routes } from '../routes/Routes';
import { getErrorMessage } from '../utils/Helper/helper';
import { isEmpty } from 'lodash';
import { useAppDispatch } from '../hooks/storeHooks';
import { logoutUser, updateUserData } from '../store/slice/sessionSlice';
import BtSmLogo from '../library/Logo/BtSmLogo';
import { validateEmail } from '../utils/validator';

const Login: React.FC<any> = (props: any) => {
  interface FormValues {
    email: string;
    password: string;
    token: string;
  }

  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');
  const errorCode = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');
  const [passwordShown, setPasswordShown] = useState<boolean>(false);
  const [isEmailBtDomain, setIsEmailBtDomain] = useState<boolean>(false);
  const [isPopupEnabled, setIsPopupEnabled] = useState<boolean>(false);
  const [isCredentialInvalid, setCredentialInvalid] = useState<boolean>(false);
  const [isNotVerified, setIsNotVerified] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [ssoLoader, setSsoLoader] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [isTempLocked, setIsTempLocked] = useState<boolean>(false);
  const [showPassField, setShowPassField] = useState<boolean>(false);
  const [loadingNext, setLoadingNext] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<string>('');
  const message = isCredentialInvalid
    ? 'Email or Password is invalid'
    : isLocked
    ? 'Acount is Locked permanantly, please connect to BT Admin'
    : isTempLocked
    ? 'Account is temporarily Locked, Please try after 10 mins'
    : isNotVerified
    ? 'User not verified or account deactivated — please contact BT Admin for assistance.'
    : 'Please wait';
  const errorMessage = ssoLoader
    ? 'Please Wait..'
    : errorCode
    ? getErrorMessage(errorDescription)
    : tokenError;
  const showSsoPopup = ssoLoader || !isEmpty(errorMessage) || !isEmpty(tokenError);

  let isLoggedIn: boolean = false;
  const navigate: NavigateFunction = useNavigate();
  const togglePassword = (e: { preventDefault: () => void }): any => {
    e.preventDefault();
    setPasswordShown(!passwordShown);
  };
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm<FormValues>();

  // Toggle between show and hide button
  const buttonText = passwordShown ? 'Hide' : 'Show';
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.endsWith('@bt.com')) {
      setIsEmailBtDomain(true);
    } else {
      setIsEmailBtDomain(false);
    }
  };

  // login method for users who want to sign-in to Organization user page
  const doLogin: SubmitHandler<FormValues> = async (data, event) => {
    event?.preventDefault();
    setVisibility(true);
    if (navigator.onLine) {
      setShowLoader(true);
      setTimeout(() => setShowLoader(false), 9000);
      const encryptedPassword = encrypt(data.password);
      const userLoginObj = new UserLogin({
        email: data.email || '',
        password: encryptedPassword || ''
      });

      sessionStorage.setItem('userPassword', encryptedPassword);
      setCredentialInvalid(false);
      const loginStatus = await userLoginObj.doLogin('');
      const responseData: any = loginStatus?.body ?? {};
      const userType = responseData?.userType ?? Number.MAX_SAFE_INTEGER;
      switch (loginStatus.statusCode) {
        case 201:
          setIsPopupEnabled(false);
          switch (userType) {
            case UserType.BtAdmin:
              void navigate(btAdminRoutes.partnerAdmin);
              break;
            case UserType.OrganizationAdmin:
              isLoggedIn = true;
              sessionStorage.setItem('loginToken', encrypt(JSON.stringify(responseData)));
              dispatch(updateUserData(JSON.stringify(responseData)));
              sessionStorage.setItem('secretCode', responseData.secretCode);
              sessionStorage.setItem(
                'accessToken',
                responseData.cognitoDetails.message.accessToken.jwtToken
              );
              sessionStorage.setItem('isMfaSetup', responseData.isMfaSetup);
              void navigate(routes.termsAndConditions);
              break;
            case UserType.OrganizationUser:
              sessionStorage.setItem('loginToken', encrypt(JSON.stringify(responseData)));
              dispatch(updateUserData(JSON.stringify(responseData)));
              sessionStorage.setItem('secretCode', responseData.secretCode);
              sessionStorage.setItem(
                'accessToken',
                responseData.cognitoDetails.message.accessToken.jwtToken
              );
              sessionStorage.setItem('isMfaSetup', responseData.isMfaSetup);
              void navigate(routes.termsAndConditions);
              break;
            default:
              sessionStorage.setItem('loginToken', encrypt(JSON.stringify(responseData)));
              dispatch(updateUserData(JSON.stringify(responseData)));
              break;
          }
          break;
        case 301:
          sessionStorage.setItem('isMfaSetup', 'true');
          void navigate(routes.termsAndConditions);
          break;
        case 423:
          setIsPopupEnabled(true);
          setIsLocked(true);
          break;
        case 429:
          setIsPopupEnabled(true);
          setIsTempLocked(true);
          break;
        case 403:
          setIsNotVerified(true);
          setIsPopupEnabled(true);
          break;
        case 401:
          setCredentialInvalid(true);
          setIsPopupEnabled(true);
          break;
        case 500:
          alert('User Email is invalid');
          void navigate(0);
          break;
        default:
          setCredentialInvalid(true);
          setIsPopupEnabled(true);
      }
      // Storing the login token and email address of the user
      sessionStorage.setItem('userEmail', data.email);
      sessionStorage.setItem('user-type', userType);
    } else {
      alert(
        'Sorry, there is not internet connection, available, please connect your device with a network.'
      );
    }
  };

  const popupCloseHandler = (e: boolean) => {
    dispatch(logoutUser());
    void navigate(0);
    setVisibility(e);
  };

  const handleSsoLogin = async () => {
    setSsoLoader(true);
    const userLoginService = new UserLogin({
      email: '',
      password: ''
    });
    const response = await userLoginService.getSsoLoginUrl();
    if (response.statusCode === 200) {
      window.location.href = response.body;
    } else {
      setTokenError('Something went wrong.');
    }
  };

  const handleSsoClick = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(getValues('email'));
    if (error) {
      setTokenError(error);
      return;
    }
    void handleSsoLogin();
  };

  const handleNextStep = (e: any) => {
    e.preventDefault();
    const error = validateEmail(getValues('email'));
    if (error) {
      setTokenError(error);
      return;
    }
    setLoadingNext(true);
    setTimeout(() => {
      setShowPassField(true);
      setLoadingNext(false);
    }, 1000);
  };

  const handleGoBack = () => {
    if (showLoader) return;
    setShowPassField(false);
  };

  const ssoLogin = async (code: string) => {
    setSsoLoader(true);
    const userLoginObj = new UserLogin({
      email: '',
      password: ''
    });
    const response = await userLoginObj.ssoLogin(code);

    if (response.statusCode === 200) {
      sessionStorage.setItem('user-type', String(UserType.BtAdmin));
      void navigate(btAdminRoutes.partnerAdmin);
    } else {
      setTokenError(response.body);
    }
    setSsoLoader(false);
  };

  const errorPopupCloseHandler = () => {
    void navigate(routes.login);
    setTokenError('');
  };

  useEffect(() => {
    if (isLoggedIn) {
      void navigate(adminRoutes.dashoard);
    } else if (code) {
      void ssoLogin(code);
    }
  }, [code]);

  return (
    <div className="h-screen min-h-fit xl:max-h-screen relative overflow-hidden flex flex-col justify-center items-center p-0">
      {/* Header start */}
      <div className="w-full flex-grow-0">
        <Header>
          <WelcomeHeader />
        </Header>
      </div>
      {/* Header End */}
      {/* Border Start */}
      <div className="w-full p-0 z-50 mt-0 clear-both h-px border-t border-[#ccc]"></div>
      {/* Border End */}
      {/* Login Panel start */}
      <div className="flex-grow w-full relative p-0 flex flex-col justify-center items-center">
        <div className="relative flex-grow m-0 p-10 z-30 flex justify-center items-center">
          <div className="min-h-[250px] min-w-[288px] w-72 sm:w-[20rem] md:w-96 max-w-[28rem] block pointer-events-auto p-4 py-6 md:p-8 md:py-10 rounded-lg shadow-lg bg-white">
            <form
              className="my-form h-full"
              method="post"
              action=""
              name="frm_login"
              onSubmit={wrapAsyncFunction(handleSubmit(doLogin))}
            >
              <div className="pb-2 -ml-[1rem]">
                <BtSmLogo />
              </div>
              {/* loginsection start */}
              <div className="w-full">
                <h2 className="text-2xl text-left font-bold mb-2">Log in</h2>
                {/* <Label htmlFor="validationCustomEmail" className="inline mb-0 mt-2">
                  {errors.email == null ? 'Email' : <span>Please enter your email address</span>}
                </Label> */}
                {showPassField ? (
                  <>
                    <button type="button" onClick={handleGoBack} className="text-sm" disabled={showLoader}>
                      {'← ' + getValues('email')}
                    </button>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="pb-0 cursor-pointer text-[11px] text-black"
                        onClick={togglePassword}
                        disabled={showLoader}
                      >
                        {buttonText}
                      </button>
                    </div>
                    <InputField
                      id={'LoginPassword'}
                      key="password"
                      className="h-10 w-full p-1.5 text-slate-400 rounded-lg rounded-bg border login-field-border my-1.5 bg-[url('https://img01.bt.co.uk/s/assets/020822/images/logintextboxbg.png')]"
                      placeholder="Password"
                      type={passwordShown ? 'text' : 'password'}
                      register={register('password', { required: true })}
                      disabled={loadingNext || showLoader}
                      style={
                        errors.password == null
                          ? { border: '1px solid #666' }
                          : { border: '1px solid #e60014' }
                      }
                    />
                  </>
                ) : (
                  <InputField
                    id={'LoginEmail'}
                    key="email"
                    className="h-10 mt-6 w-full p-1.5 text-slate-400 rounded-lg rounded-bg border login-field-border my-1.5 bg-[url('https://img01.bt.co.uk/s/assets/020822/images/logintextboxbg.png')]"
                    type="email"
                    placeholder="Email"
                    register={register('email', {
                      onChange: handleEmailChange,
                      maxLength: 120,
                      required: true
                    })}
                    disabled={loadingNext || ssoLoader}
                    maxLength={120}
                    style={
                      errors.email == null
                        ? { border: '1px solid #666' }
                        : { border: '1px solid #e60014' }
                    }
                  />
                )}
                <p className="mt-2 text-left clear-both text-sm pb-0 m-0 text-[#666]">
                  <Link className="text-sm text-black underline" to={'/reset/otp'}>
                    Forgot my password
                  </Link>
                </p>
                {showPassField && (
                  <>
                    <Button
                      className="p-2 mb-3 mt-1 tracking-normal h-11 text-[#fff] text-md w-full shadow-none border cursor-pointer bg-black rounded-lg"
                      buttonText="Login"
                      loading={showLoader}
                      disabled={showLoader}
                    />
                  </>
                )}
              </div>
              {isPopupEnabled && (
                <CustomPopup onClose={popupCloseHandler} show={visibility} width={'w-41%'}>
                  <div className="w-60 flex">
                    <p className="text-center">{message}</p>
                  </div>
                </CustomPopup>
              )}
              <CustomPopup onClose={errorPopupCloseHandler} show={showSsoPopup} width={'w-41%'}>
                <div className="w-60 block">
                  <p className="text-center">{errorMessage}</p>
                </div>
              </CustomPopup>
            </form>
            {!showPassField && (
              <>
                <div className="flex overflow-hidden mt-2">
                  <Button
                    className={
                      isEmailBtDomain
                        ? 'p-2 mr-2 tracking-normal h-11 text-black text-md w-full shadow-none border cursor-pointer bg-white rounded-lg'
                        : 'p-2 tracking-normal h-11 text-white text-md w-full shadow-none border cursor-pointer bg-black rounded-lg'
                    }
                    buttonText="Next"
                    loadingColour={isEmailBtDomain ? 'black' : 'white'}
                    onclick={handleNextStep}
                    loading={loadingNext}
                    disabled={loadingNext || ssoLoader}
                  />
                  <input type="checkbox" className="hidden peer" checked={isEmailBtDomain} />
                  <Button
                    className="h-11 tracking-normal text-white text-md w-0 p-0 opacity-0 peer-checked:w-full peer-checked:p-2 peer-checked:opacity-100 shadow-none border cursor-pointer bg-black rounded-lg transition-all ease-out duration-200 whitespace-nowrap overflow-hidden"
                    buttonText="SSO Login"
                    onclick={handleSsoClick}
                    loading={ssoLoader}
                    disabled={ssoLoader}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {/* Login Panel End */}
        {/* Background Image start */}
        <div className="h-full w-full mt-0 mr-auto mb-0 min-h-[64.000em] absolute top-0 text-center z-0 block overflow-hidden">
          <picture className="h-full">
            <img
              src={backgroundImage}
              className="h-full relative object-cover bg-center bg-cover bg-repeat w-full"
              alt="Home"
              height="auto"
              width="100%"
            />
          </picture>
        </div>
        <div className="h-full w-full mt-0 mr-auto mb-0 min-h-[64.000em] absolute top-0 text-center block overflow-hidden bg-black z-10 opacity-10"></div>
        {/* Background Image End */}
        {/* Footer Start */}
        <div className="w-full flex-grow-0">
          <Footer />
        </div>
      </div>
      {/* Footer End */}
    </div>
  );
};
export default Login;
